import { EMPTY_LESSON_SUBJECT, type DiaryLesson } from '@/types/diary';

const createEmptyLesson = (lessonNumber: number): DiaryLesson => ({
  className: EMPTY_LESSON_SUBJECT,
  lessonNumber,
  isOnlyForCurrentWeek: false,
  topics: [],
  homeTasks: [],
  thirdPartyEvents: [],
  evaluations: [],
  videoMeetings: [],
  scheduledTests: [],
});

const hasNumber = (
  lesson: DiaryLesson,
): lesson is DiaryLesson & { lessonNumber: number } =>
  lesson.lessonNumber !== undefined;

export const fillEmptyLessons = (lessons: DiaryLesson[]): DiaryLesson[] => {
  const numberedLessons = lessons.filter(hasNumber);

  if (!numberedLessons.length) {
    return lessons;
  }

  const lessonNumbers = numberedLessons.map((lesson) => lesson.lessonNumber);
  const firstLessonNumber = Math.min(...lessonNumbers);
  const lastLessonNumber = Math.max(...lessonNumbers);
  const hasSecondLesson = lessonNumbers.includes(2);
  const hasLessonsFromThird = lessonNumbers.some((number) => number >= 3);

  const filledLessons: DiaryLesson[] = [];

  for (let number = 0; number <= lastLessonNumber; number += 1) {
    const lessonsAtNumber = numberedLessons.filter(
      (lesson) => lesson.lessonNumber === number,
    );

    if (lessonsAtNumber.length) {
      filledLessons.push(...lessonsAtNumber);
      continue;
    }

    const shouldSkipEmptyLesson =
      number === 0 ||
      (number === 1
        ? !hasSecondLesson && !hasLessonsFromThird
        : number < firstLessonNumber);

    if (!shouldSkipEmptyLesson) {
      filledLessons.push(createEmptyLesson(number));
    }
  }

  return [...filledLessons, ...lessons.filter((lesson) => !hasNumber(lesson))];
};
